import _ from 'lodash'
import Guard from 'arg-guard'

import AppError from '@/models/app-error'
import CALCULATION_STATES from '@/models/CALCULATION_STATES'
import Logger from '@/logger'

import { MUTATIONS } from '../../mutations'

const ACTIONS = {

    clearProjectData: 'clear-project-data',

    clearCurrentProjectLoadFailure: 'clear-current-project-load-failure',
    flagCurrentProjectInitialised: 'flag-current-project-initialised',
    setCalculationNotReady: 'set-calculation-not-ready',

    appendSavingsDataAsync: 'append-savings-data',
    updateMetricsFromSavings: 'update-metrics-from-savings',

    deselectImprovements: 'deselect-improvements',
    selectImprovements: 'select-improvements',
    recommendImprovements: 'recommend-improvements',
    clearRecommendations: 'clear-recommendations',
    sortImprovements: 'sort-improvements',
}

export { ACTIONS }

export default {

    /**
     * Loads any specific savings combinations into state, to augment the initial data load.
     * @param {Object} context
     * @param {Object} { dataLoader {Function}, improvementIds {Array} }
     */
    async [ACTIONS.appendSavingsDataAsync](context, { dataLoader, improvementIds }) {
        Guard.throwIf(dataLoader, 'dataLoader')

        try {
            const result = improvementIds
                ? await dataLoader(context.rootState.property.id, improvementIds)
                : await dataLoader(context.rootState.property.id)
            context.commit(MUTATIONS.IMPROVEMENTS.appendSavings, result)
        } catch (err) {
            Logger.logObject(err, `Fail state on ${ ACTIONS.appendSavingsDataAsync }`, 'Improvements')
            throw err
        }
    },

    /**
     * Clears a flag that says that the improvements data load has failed.
     * @param {Object} context
     */
    [ACTIONS.clearCurrentProjectLoadFailure](context) {
        if (context.state.loadFailed)
            context.commit(MUTATIONS.IMPROVEMENTS.setCurrentProjectLoadFailState, false)
    },

    /**
     * Clears all the improvement data and savings for the project.
     * @param {Object} context
     */
    [ACTIONS.clearProjectData](context) {
        // noinspection JSIgnoredPromiseFromCall
        context.dispatch(ACTIONS.clearCurrentProjectLoadFailure)
        context.commit(MUTATIONS.IMPROVEMENTS.setCurrentProjectImprovements, { sourceData: [] })
        context.commit(MUTATIONS.IMPROVEMENTS.clearSavings)
        context.commit(MUTATIONS.IMPROVEMENTS.setImprovementCalculationState, CALCULATION_STATES.pending)
    },

    /**
     * Clears the recommended flag from each of the provided improvements.
     * @param {Object} context
     * @param {Array} improvements The improvements to clear.
     */
    [ACTIONS.clearRecommendations](context, improvements) {
        improvements.forEach(imp => context.commit(MUTATIONS.IMPROVEMENTS.clearRecommendationFromImprovement, imp.id))
    },

    /**
     * Deselects each of the provided improvements.
     * @param {Object} context
     * @param {Array} improvements The improvements to deselect.
     */
    [ACTIONS.deselectImprovements](context, improvements) {
        improvements.forEach(imp => context.commit(MUTATIONS.IMPROVEMENTS.deselectImprovement, imp.id))
    },

    /**
     * Indicates that the improvements state has been initialised.
     * @param {Object} context
     */
    [ACTIONS.flagCurrentProjectInitialised](context) {
        if (!context.state.currentProject.initialised)
            context.commit(MUTATIONS.IMPROVEMENTS.setCurrentProjectInitialisationState, true)
    },

    /**
     * Recommends each of the provided improvements.
     * @param {Object} context
     * @param {Array} improvements The improvements to recommend.
     */
    [ACTIONS.recommendImprovements](context, improvements) {
        improvements.forEach(imp => context.commit(MUTATIONS.IMPROVEMENTS.recommendImprovement, imp.id))
    },

    /**
     * Sets calculation as not ready.
     * @param {Object} context
     */
    [ACTIONS.setCalculationNotReady](context) {
        Logger.error('Unable to load project data at this time')
        context.commit(MUTATIONS.logAppError, new AppError('Unable to load project data at this time', true))
        context.commit(MUTATIONS.IMPROVEMENTS.setImprovementCalculationState, 1)
    },

    /**
     * Selects each of the provided improvements.
     * @param {Object} context
     * @param {Array} improvements The improvements to select.
     */
    [ACTIONS.selectImprovements](context, improvements) {
        improvements.forEach(imp => context.commit(MUTATIONS.IMPROVEMENTS.selectImprovement, imp.id))
    },

    /**
     * Takes a sorted list of improvements or any objects with an ID and uses that sort order to order the improvements, leaving other improvements at the end of the list.
     * @param {Object} context
     * @param {Array} sortOrder The sorted improvements to use for ordering.
     */
    [ACTIONS.sortImprovements](context, sortOrder) {
        const projectImprovements = context.state.currentProject.improvements
        const sourceData = []
        const remaining = projectImprovements ? _.cloneDeep(projectImprovements) : []
        const baseMetrics = context.rootState.metrics.data.map(m => {
            return { key: m.key, value: m.current }
        })

        sortOrder.forEach(imp => {
            const itemInSort = remaining.find(rem => rem.id === imp.id)
            if (itemInSort) {
                sourceData.push(itemInSort)
                _.pull(remaining, itemInSort)
            }
        })
        sourceData.push(...remaining)
        context.commit(MUTATIONS.IMPROVEMENTS.setCurrentProjectImprovements, { sourceData, baseMetrics })
    },

    /**
     * Updates metric projections.
     * @param {Object} context
     * @param {Array} changes A selection of changes to apply to each metric value.
     */
    [ACTIONS.updateMetricsFromSavings](context, changes) {
        context.commit(MUTATIONS.METRICS.updateProjections, changes)
        return changes
    },
}
